import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ChevronDown, UtensilsCrossed } from 'lucide-react';
import { menuData } from './menuData';
import { allergenData } from './allergenData';
import { imageMapping } from './imageMapping';
import { useTranslation } from '../../contexts/TranslationContext';
import AllergenIcon from './AllergenIcons';
import MagneticButton from '../common/MagneticButton';

gsap.registerPlugin(ScrollTrigger);

const Menu: React.FC = () => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Reveal cards on scroll
      gsap.utils.toArray<HTMLElement>('.menu-card').forEach((card) => {
        gsap.from(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            toggleActions: 'play none none reverse'
          },
          y: 40,
          opacity: 0,
          duration: 1,
          ease: 'power3.out'
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const toggleSection = (id: string, forceScroll = true) => {
    setOpenSections(prev => {
      const isOpen = prev.includes(id);
      if (!isOpen) {
        if (forceScroll) {
          setTimeout(() => {
            const element = document.getElementById(id);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 150);
        }
        return [...prev, id];
      } else {
        return prev.filter(s => s !== id);
      }
    });
  };

  const formatProductName = (nameKey: string) => {
    const name = t(nameKey);
    const parts = name.split(/(\(.*?\))/);
    return parts.map((part, i) =>
      part.startsWith('(') && part.endsWith(')')
        ? <span key={i} className="italic text-[0.85em] opacity-60 ml-1 font-sans">{part}</span>
        : part
    );
  };

  const renderProductItem = (item: any, index: number) => {
    const imageName = imageMapping[item.originalName];
    const hasRestaurantPhoto = imageName && imageName.length > 0;
    const imageUrl = hasRestaurantPhoto ? `/images/${imageName}` : null;
    const allergens = allergenData[item.originalName] || [];

    return (
      <div
        key={index}
        className="group relative bg-white/40 border border-burgundy/15 hover:border-burgundy/40 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-xl hover:shadow-earth-dark/5 flex flex-col h-full"
      >
        {imageUrl && (
          <div
            className="relative h-20 overflow-hidden bg-earth-light/5 cursor-zoom-in"
            onClick={() => setSelectedImage(imageUrl)}
          >
            <img
              src={imageUrl}
              alt={item.originalName}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
          </div>
        )}

        <div className="p-5 flex flex-col flex-grow space-y-3">
          <div className="flex justify-between items-baseline gap-4">
            <h4 className={`text-xl font-serif leading-snug flex-grow ${item.highlight ? 'text-burgundy font-bold' : 'text-earth-dark'}`}>
              {formatProductName(item.nameKey)}
            </h4>
            {item.price && (
              <span className="font-serif font-bold text-burgundy text-lg whitespace-nowrap">
                {item.price}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1.5 mt-auto">
            {allergens.map((alg, i) => (
              <AllergenIcon key={i} allergen={alg} size={16} />
            ))}
          </div>
        </div>
      </div>
    );
  };

  const Accordion = ({ id, titleKey, children, isNested }: { id: string, titleKey: string, children: React.ReactNode, isNested?: boolean }) => {
    const isOpen = openSections.includes(id);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (isOpen && contentRef.current) {
        gsap.fromTo(contentRef.current,
          { height: 0, opacity: 0 },
          { height: 'auto', opacity: 1, duration: 0.6, ease: 'power2.out' }
        );
      }
    }, [isOpen]);

    return (
      <div id={id} className={`menu-card ${isNested ? 'mb-4 border-l-2 border-burgundy/10 ml-4' : 'mb-16 premium-card overflow-hidden shadow-sm'}`}>
        <button
          onClick={() => toggleSection(id)}
          className={`w-full flex items-center justify-between transition-all duration-500 ${isNested ? 'px-6 py-3 hover:bg-burgundy/5 rounded-xl' : 'px-10 py-5 hover:bg-white/40'} ${isOpen ? 'bg-burgundy/[0.03]' : ''}`}
        >
          <div className="flex flex-col items-start gap-1">
            <span className={`font-serif text-earth-dark tracking-tighter lowercase italic ${isNested ? 'text-2xl md:text-3xl' : 'text-3xl md:text-4xl'}`}>
              {t(titleKey)}
            </span>
            <div className={`h-0.5 bg-burgundy/30 transition-all duration-500 ${isOpen ? 'w-full' : 'w-12'}`} />
          </div>
          <ChevronDown className={`text-burgundy transition-transform duration-700 ${isOpen ? 'rotate-180' : ''}`} size={isNested ? 24 : 28} strokeWidth={1.5} />
        </button>

        {isOpen && (
          <div ref={contentRef} className={`${isNested ? 'px-4 pt-4 pb-6' : 'px-6 md:px-12 pb-12'}`}>
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="max-w-6xl mx-auto px-6 py-8 space-y-8">
      {/* Lightbox / Popup */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-2xl flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-300"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-full max-h-full flex items-center justify-center">
            <img
              src={selectedImage}
              alt="Product detail"
              className="max-w-[95vw] max-h-[95vh] object-contain rounded-2xl shadow-2xl animate-in zoom-in-95 duration-500 border border-white/10"
            />
            <button
              className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 p-4 rounded-full backdrop-blur-md transition-colors shadow-xl"
              onClick={() => setSelectedImage(null)}
            >
              <div className="w-8 h-8 flex items-center justify-center font-bold text-white text-3xl">×</div>
            </button>
          </div>
        </div>
      )}

      {/* CARTA GENERAL */}
      <div className="pt-4">
        <Accordion id="carta-general" titleKey="carta_general_title">
          <div className="space-y-4 pt-6">
            {[
              { id: 'entrantes', key: 'entrantes_title', data: menuData.entrantes },
              { id: 'ensaladas', key: 'ensaladas_title', data: menuData.ensaladas },
              { id: 'ibericos', key: 'ibericos_title', data: menuData.ibericos },
              { id: 'carnes', key: 'carnes_title', data: menuData.carnes },
              { id: 'pescados', key: 'pescados_title', data: menuData.pescados },
              { id: 'platos-combinados', key: 'platos_combinados_title', data: menuData.platosCombinados },
            ].map((section) => (
              <Accordion key={section.id} id={section.id} titleKey={section.key} isNested>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
                  {section.data.map(renderProductItem)}
                </div>
              </Accordion>
            ))}
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-30 text-center mt-24">{menuData.cartaPrincipalNotes}</p>
          </div>
        </Accordion>

        {/* MENÚ DEL DÍA */}
        <Accordion id="menu-del-dia" titleKey="menu_de_la_casa_title">
          <div className="space-y-4 pt-6">
            <Accordion id="md-primeros" titleKey="primeros_platos" isNested>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
                {menuData.menuDelDia.primeros.map(renderProductItem)}
              </div>
            </Accordion>

            <Accordion id="md-segundos" titleKey="segundos_platos" isNested>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
                {menuData.menuDelDia.segundos.map(renderProductItem)}
              </div>
            </Accordion>

            <Accordion id="md-postres" titleKey="postres" isNested>
              <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
                {menuData.menuDelDia.postres.map(renderProductItem)}
              </div>
            </Accordion>

            {/* PRICES MOVED TO BOTTOM OF SECTION */}
            <div className="grid md:grid-cols-2 gap-8 bg-burgundy/[0.03] p-10 rounded-[2.5rem] border border-burgundy/10 backdrop-blur-sm shadow-inner mt-12">
              <div className="space-y-3 text-center md:text-left">
                <h5 className="font-sans uppercase tracking-[0.3em] text-[10px] font-bold text-burgundy opacity-50">
                  {t('menu_del_dia_price_laborales')}
                </h5>
                <div className="flex items-baseline justify-center md:justify-start gap-2">
                  <p className="font-serif text-6xl text-burgundy">18</p>
                  <span className="text-2xl text-burgundy opacity-60">€</span>
                </div>
              </div>
              <div className="space-y-3 text-center md:text-left">
                <h5 className="font-sans uppercase tracking-[0.3em] text-[10px] font-bold text-burgundy opacity-50">
                  {t('menu_del_dia_price_festivos')}
                </h5>
                <div className="flex items-baseline justify-center md:justify-start gap-2">
                  <p className="font-serif text-6xl text-burgundy">20</p>
                  <span className="text-2xl text-burgundy opacity-60">€</span>
                </div>
              </div>
            </div>

            <div className="bg-earth-dark/[0.02] p-10 rounded-[2.5rem] border border-earth-light/5 text-sm italic text-earth-dark/50 leading-loose space-y-4">
              <p className="flex gap-4"><strong className="text-burgundy opacity-80 uppercase tracking-widest text-[10px] shrink-0 pt-1">{t('includes')}</strong> {t('menu_del_dia_includes')}</p>
              <p className="flex gap-4"><strong className="text-burgundy opacity-80 uppercase tracking-widest text-[10px] shrink-0 pt-1">{t('supplement')}</strong> {t('menu_del_dia_supplement')}</p>
              <p className="opacity-80 pt-4 border-t border-earth-light/10">{t('menu_del_dia_notes')}</p>
            </div>
          </div>
        </Accordion>

        {/* MENÚ INFANTIL */}
        <Accordion id="menu-infantil" titleKey="menu_infantil_title">
          <div className="space-y-14 pt-10">
            <div className="grid lg:grid-cols-2 gap-16">
              <section className="space-y-10">
                <h3 className="text-3xl italic lowercase tracking-tight">{t('primeros_platos')}</h3>
                <div className="grid gap-6">
                  {menuData.menuInfantil.primeros.map(renderProductItem)}
                </div>
              </section>
              <section className="space-y-10">
                <h3 className="text-3xl italic lowercase tracking-tight">{t('segundos_platos')}</h3>
                <div className="grid gap-6">
                  {menuData.menuInfantil.segundos.map(renderProductItem)}
                </div>
              </section>
            </div>

            <div className="bg-burgundy/[0.03] p-12 rounded-[3rem] border border-burgundy/10 text-center">
              <h5 className="font-sans uppercase tracking-[0.4em] text-[10px] font-bold text-burgundy opacity-60 mb-4">
                {t('price')}
              </h5>
              <div className="flex items-baseline justify-center gap-2">
                <p className="font-serif text-7xl text-burgundy">{menuData.menuInfantil.price.replace('€', '')}</p>
                <span className="text-3xl text-burgundy opacity-60">€</span>
              </div>
            </div>

            <div className="bg-earth-dark/[0.02] p-10 rounded-[2.5rem] text-sm italic text-earth-dark/50 space-y-2">
              <p><strong>{t('conditions')}:</strong> {t('age_limit_note')}</p>
              <p><strong>{t('includes')}:</strong> {t('menu_infantil_includes')}</p>
              <p><strong>{t('schedule')}:</strong> {menuData.menuInfantil.schedule}</p>
              <p>{t('menu_infantil_notes')}</p>
            </div>
          </div>
        </Accordion>

        {/* CARTA BARRA */}
        <Accordion id="carta-barra" titleKey="carta_barra_title">
          <div className="space-y-4 pt-6">
            {[
              { id: 'cb-pinchos', key: 'pinchos_title', data: menuData.cartaBarraPinchos },
              { id: 'cb-ibericos', key: 'ibericos_title', data: menuData.cartaBarraIbericos },
              { id: 'cb-montaditos', key: 'montaditos_title', data: menuData.cartaBarraMontaditos },
            ].map((section) => (
              <Accordion key={section.id} id={section.id} titleKey={section.key} isNested>
                <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-6">
                  {section.data.map(renderProductItem)}
                </div>
              </Accordion>
            ))}
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-30 text-center mt-24">{menuData.cartaBarraNotes}</p>
          </div>
        </Accordion>

        <div className="flex justify-center pt-12">
          <MagneticButton className="px-12 py-5 bg-burgundy text-white rounded-full font-serif text-xl shadow-2xl shadow-burgundy/20 hover:bg-burgundy-dark tracking-tighter">
            {t('welcome_instruction')}
          </MagneticButton>
        </div>
      </div>
    </div>
  );
};

export default Menu;
